import photos from '@/content/photos.json'
import type { Photo } from '@/content/sections'

/**
 * A photograph, served at the size the layout actually needs.
 *
 * The manifest is written by `pnpm images` and keyed by the original's path, so
 * this component's whole job is: look the photograph up, emit a `srcset`, and
 * tell the browser how wide it will be rendered. Getting `sizes` wrong is the
 * expensive mistake — the default is `100vw`, so a photograph rendered at half
 * width downloads the file for a full-width one and every phone pays for it.
 *
 * A missing key renders nothing rather than throwing. In a preview it also says
 * so in the console: a typo in a filename should show up as an absent photograph
 * during review, not as a blank page.
 */
type Manifest = Record<string, { width: number; height: number; webp: { w: number; src: string }[]; fallback: string }>

export default function SitePhoto({
  photo,
  sizes,
  className,
  priority,
}: {
  photo: Photo
  /** What width this will actually render at. Always pass it. */
  sizes: string
  className?: string
  /**
   * The hero, and only the hero. It is the largest image above the fold on every
   * page, so it loads eagerly and gets fetch priority; everything else is lazy,
   * because a photograph three screens down competing with the hero for bandwidth
   * makes the page slower to become readable.
   */
  priority?: boolean
}) {
  const entry = (photos as Manifest)[photo.src]
  if (!entry) {
    if (import.meta.env.DEV) console.warn(`no photo in the manifest for ${photo.src}`)
    return null
  }

  return (
    <img
      src={entry.fallback}
      srcSet={entry.webp.map((v) => `${v.src} ${v.w}w`).join(', ')}
      sizes={sizes}
      // The intrinsic size, so the browser reserves the space before the bytes
      // arrive and the text below it does not jump.
      width={entry.width}
      height={entry.height}
      alt={photo.alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      decoding={priority ? 'sync' : 'async'}
      className={className}
    />
  )
}
